import { Badge, Button, HelpTooltip, LearnMoreLink } from '@auctisync/ui';
import { dollarsToCents, type Tables, type TablesInsert } from '@auctisync/shared';
import Papa from 'papaparse';
import { useEffect, useState, type ChangeEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { readClaims, useSession } from '../../hooks/useSession';
import { supabase } from '../../lib/supabase';

type ItemCategory = Pick<Tables<'item_categories'>, 'id' | 'name' | 'slug'>;
type Consignor = Pick<Tables<'consignors'>, 'id' | 'display_name' | 'tenant_consignor_number'>;
type TenantOption = Pick<Tables<'tenants'>, 'id' | 'slug' | 'name'>;

type ParsedRow = {
  rowNumber: number;
  payload: TablesInsert<'inventory_items'> | null;
  errors: string[];
  preview: {
    title: string;
    category: string;
    consignor: string;
    qty: string;
    startingBid: string;
  };
};

const SUPPORTED_COLUMNS = [
  'control_number',
  'title',
  'title_override',
  'description',
  'description_override',
  'condition',
  'category',
  'consignor',
  'qty_on_hand',
  'retail_price',
  'starting_bid',
  'reserve',
  'estimate_min',
  'estimate_max',
  'msrp',
  'mpn',
  'upc',
  'sku',
  'location_bin',
  'location_notes',
  'is_visible',
] as const;

type SupportedColumn = (typeof SUPPORTED_COLUMNS)[number];

function normalizeHeader(h: string): string {
  return h.trim().toLowerCase().replace(/\s+/g, '_').replace(/-+/g, '_');
}

function parseBool(v: string): boolean {
  const t = v.trim().toLowerCase();
  return t === 'true' || t === 'yes' || t === '1' || t === 'y';
}

function parseDollars(v: string): number | null {
  const cleaned = v.replace(/[,$\s]/g, '');
  if (cleaned === '') return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n < 0) return null;
  return dollarsToCents(n);
}

/**
 * CSV import for inventory. Phase 1: field-matched (exact column name),
 * validate + preview + insert. Full column-mapping UI is Phase 2.
 *
 * Known limitations:
 * - Update-on-conflict (control_number match → update) is documented but not
 *   yet implemented — duplicates will be reported as errors.
 * - No image-URL import from CSV (use the inventory detail Photo Manager).
 * - No jsonb custom_fields import.
 */
export function AdminInventoryImport() {
  const navigate = useNavigate();
  const { session } = useSession();
  const claims = readClaims(session);

  const [categories, setCategories] = useState<ItemCategory[]>([]);
  const [consignors, setConsignors] = useState<Consignor[]>([]);

  const [tenantOptions, setTenantOptions] = useState<TenantOption[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>(claims.tenantId ?? '');
  const needsTenantPicker = claims.isSuperAdmin && !claims.tenantId;

  const [rawHeaders, setRawHeaders] = useState<string[]>([]);
  const [matchedColumns, setMatchedColumns] = useState<SupportedColumn[]>([]);
  const [ignoredColumns, setIgnoredColumns] = useState<string[]>([]);
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number }>({ done: 0, total: 0 });
  const [result, setResult] = useState<{ inserted: number; failed: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const tenantId = needsTenantPicker ? selectedTenantId : claims.tenantId;

  useEffect(() => {
    if (!tenantId) return;
    void (async () => {
      const [{ data: cats }, { data: cons }] = await Promise.all([
        supabase
          .from('item_categories')
          .select('id, name, slug')
          .eq('tenant_id', tenantId)
          .is('archived_at', null),
        supabase
          .from('consignors')
          .select('id, display_name, tenant_consignor_number')
          .eq('tenant_id', tenantId)
          .eq('status', 'active'),
      ]);
      setCategories(cats ?? []);
      setConsignors(cons ?? []);
    })();
  }, [tenantId]);

  useEffect(() => {
    if (!needsTenantPicker) return;
    void (async () => {
      const { data } = await supabase
        .from('tenants')
        .select('id, slug, name')
        .eq('status', 'active')
        .order('name', { ascending: true });
      const list = data ?? [];
      setTenantOptions(list);
      const first = list[0];
      if (first && !selectedTenantId) setSelectedTenantId(first.id);
    })();
  }, [needsTenantPicker, selectedTenantId]);

  function resolveCategory(raw: string): string | null {
    if (!raw.trim()) return null;
    const target = raw.trim().toLowerCase();
    const match = categories.find(
      (c) => c.name.toLowerCase() === target || c.slug.toLowerCase() === target,
    );
    return match?.id ?? null;
  }

  function resolveConsignor(raw: string): string | null {
    if (!raw.trim()) return null;
    const target = raw.trim().toLowerCase();
    const byNum = Number.isInteger(Number(raw)) ? Number(raw) : null;
    const match = consignors.find(
      (c) =>
        c.display_name.toLowerCase() === target ||
        (byNum !== null && c.tenant_consignor_number === byNum),
    );
    return match?.id ?? null;
  }

  function parseRow(
    rowIdx: number,
    raw: Record<string, string>,
    resolvedTenantId: string,
  ): ParsedRow {
    const errors: string[] = [];
    const get = (col: SupportedColumn): string => raw[col] ?? '';

    const title = get('title').trim();
    const titleOverride = get('title_override').trim();
    const description = get('description').trim();
    const descriptionOverride = get('description_override').trim();

    if (!title && !titleOverride && !description && !descriptionOverride) {
      errors.push('Need at least a title or description.');
    }

    const categoryRaw = get('category');
    let category_id: string | null = null;
    if (categoryRaw.trim()) {
      category_id = resolveCategory(categoryRaw);
      if (!category_id) errors.push(`Unknown category: "${categoryRaw.trim()}".`);
    }

    const consignorRaw = get('consignor');
    let consignor_id: string | null = null;
    if (consignorRaw.trim()) {
      consignor_id = resolveConsignor(consignorRaw);
      if (!consignor_id) errors.push(`Unknown consignor: "${consignorRaw.trim()}".`);
    }

    const qtyRaw = get('qty_on_hand').trim();
    let qty_on_hand = 1;
    if (qtyRaw !== '') {
      const n = parseInt(qtyRaw, 10);
      if (!Number.isFinite(n) || n < 0) errors.push(`Invalid qty_on_hand: "${qtyRaw}".`);
      else qty_on_hand = n;
    }

    const retail = parseDollars(get('retail_price'));
    const starting = parseDollars(get('starting_bid'));
    const reserve = parseDollars(get('reserve'));
    const estMin = parseDollars(get('estimate_min'));
    const estMax = parseDollars(get('estimate_max'));
    const msrp = parseDollars(get('msrp'));

    const isVisibleRaw = get('is_visible');
    const is_visible = isVisibleRaw.trim() === '' ? true : parseBool(isVisibleRaw);

    const payload: TablesInsert<'inventory_items'> = {
      tenant_id: resolvedTenantId,
      control_number: get('control_number').trim() || null,
      title: title || null,
      title_override: titleOverride || null,
      description: description || null,
      description_override: descriptionOverride || null,
      condition: get('condition').trim() || null,
      category_id,
      consignor_id,
      qty_on_hand,
      retail_price_cents: retail,
      starting_bid_cents: starting,
      reserve_cents: reserve,
      estimate_min_cents: estMin,
      estimate_max_cents: estMax,
      msrp_cents: msrp,
      mpn: get('mpn').trim() || null,
      upc: get('upc').trim() || null,
      sku: get('sku').trim() || null,
      location_bin: get('location_bin').trim() || null,
      location_notes: get('location_notes').trim() || null,
      is_visible,
    };

    return {
      rowNumber: rowIdx + 2, // 1-indexed, plus header row
      payload: errors.length > 0 ? null : payload,
      errors,
      preview: {
        title: titleOverride || title || '—',
        category: categoryRaw || '—',
        consignor: consignorRaw || '—',
        qty: String(qty_on_hand),
        startingBid: starting !== null ? `$${(starting / 100).toFixed(2)}` : '—',
      },
    };
  }

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!tenantId) {
      setError('Pick a tenant first.');
      return;
    }

    setParsing(true);
    setError(null);
    setResult(null);
    setRows([]);

    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: false,
      transformHeader: (h) => normalizeHeader(h),
      complete: (results) => {
        const headers: string[] = results.meta.fields ?? [];
        setRawHeaders(headers);
        const matched: SupportedColumn[] = [];
        const ignored: string[] = [];
        for (const h of headers) {
          if ((SUPPORTED_COLUMNS as readonly string[]).includes(h)) {
            matched.push(h as SupportedColumn);
          } else {
            ignored.push(h);
          }
        }
        setMatchedColumns(matched);
        setIgnoredColumns(ignored);

        const parsed = results.data.map((row, i) => parseRow(i, row, tenantId));
        setRows(parsed);
        setParsing(false);
      },
      error: (err) => {
        setError(err.message);
        setParsing(false);
      },
    });
    event.target.value = '';
  }

  async function handleImport() {
    const validRows = rows.filter((r) => r.payload !== null);
    if (validRows.length === 0) return;

    setImporting(true);
    setError(null);
    setProgress({ done: 0, total: validRows.length });

    let inserted = 0;
    let failed = 0;

    const BATCH = 50;
    for (let i = 0; i < validRows.length; i += BATCH) {
      const slice = validRows.slice(i, i + BATCH);
      const payloads = slice
        .map((r) => r.payload!)
        .filter((p): p is TablesInsert<'inventory_items'> => p !== null);
      const { error: err, data } = await supabase
        .from('inventory_items')
        .insert(payloads)
        .select('id');
      if (err) {
        failed += slice.length;
      } else {
        inserted += data?.length ?? 0;
      }
      setProgress({ done: Math.min(i + BATCH, validRows.length), total: validRows.length });
    }

    setImporting(false);
    setResult({ inserted, failed });
  }

  const validCount = rows.filter((r) => r.payload !== null).length;
  const invalidCount = rows.length - validCount;

  return (
    <div>
      <header style={{ marginBottom: '1.5rem' }}>
        <div style={{ marginBottom: '0.5rem' }}>
          <Link
            to="/admin/inventory"
            style={{ color: 'var(--brand-accent)', fontSize: '0.875rem' }}
          >
            ← Inventory
          </Link>
        </div>
        <h1
          style={{
            fontFamily: 'var(--font-heading-active)',
            fontSize: '1.875rem',
            fontWeight: 700,
            color: 'var(--text)',
            marginBottom: '0.25rem',
          }}
        >
          Import inventory from CSV
        </h1>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', marginBottom: '0.35rem' }}>
          Field-matched import. Column headers must match the documented names exactly.
        </p>
        <LearnMoreLink helpSlug="csv-import" newTab>
          CSV column reference
        </LearnMoreLink>
      </header>

      {needsTenantPicker && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            borderRadius: 'var(--r-tile, 0.375rem)',
            background: 'var(--surface-muted, #fef3c7)',
            border: '1px solid var(--admin-hairline, var(--border))',
            fontSize: '0.875rem',
            flexWrap: 'wrap',
          }}
        >
          <Badge variant="outline">Super admin</Badge>
          <span>Importing for tenant:</span>
          <select
            value={selectedTenantId}
            onChange={(e) => setSelectedTenantId(e.target.value)}
            style={selectStyle}
          >
            <option value="">— Pick a tenant —</option>
            {tenantOptions.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.slug})
              </option>
            ))}
          </select>
        </div>
      )}

      <section
        style={{
          padding: '1.5rem',
          border: '1px solid var(--admin-hairline, var(--border))',
          borderRadius: 'var(--r-card, 0.5rem)',
          background: 'var(--surface-elev)',
          marginBottom: '1rem',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            flexWrap: 'wrap',
          }}
        >
          <label>
            <input
              type="file"
              accept=".csv,text/csv"
              disabled={parsing || importing || !tenantId}
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <Button type="button" variant="outline" asChild disabled={!tenantId}>
              <span style={{ cursor: 'pointer' }}>{parsing ? 'Parsing…' : 'Choose CSV file'}</span>
            </Button>
          </label>
          <HelpTooltip helpSlug="csv-import">
            Headers match case-insensitively. Unknown columns are ignored.
          </HelpTooltip>
          {rawHeaders.length > 0 && (
            <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
              Parsed {rows.length} row{rows.length === 1 ? '' : 's'} · {matchedColumns.length}{' '}
              matched column{matchedColumns.length === 1 ? '' : 's'}
              {ignoredColumns.length > 0 && ` · ${ignoredColumns.length} ignored`}
            </span>
          )}
        </div>

        {ignoredColumns.length > 0 && (
          <p style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            Ignored columns: {ignoredColumns.join(', ')}
          </p>
        )}
      </section>

      {error && <AlertBox variant="danger">{error}</AlertBox>}

      {rows.length > 0 && (
        <>
          <section
            style={{
              padding: '0.75rem 1rem',
              borderRadius: 'var(--r-card, 0.5rem)',
              background: 'var(--surface-elev)',
              border: '1px solid var(--admin-hairline, var(--border))',
              marginBottom: '1rem',
              display: 'flex',
              gap: '1rem',
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <strong style={{ color: 'var(--text)' }}>{validCount} valid</strong>
              {invalidCount > 0 && (
                <span style={{ color: 'var(--text-danger, #991b1b)' }}>
                  {invalidCount} with errors
                </span>
              )}
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setRows([]);
                  setRawHeaders([]);
                  setMatchedColumns([]);
                  setIgnoredColumns([]);
                  setResult(null);
                }}
                disabled={importing}
              >
                Reset
              </Button>
              <Button
                type="button"
                disabled={importing || validCount === 0}
                onClick={() => {
                  void handleImport();
                }}
              >
                {importing
                  ? `Importing ${progress.done}/${progress.total}…`
                  : `Import ${validCount} row${validCount === 1 ? '' : 's'}`}
              </Button>
            </div>
          </section>

          {result && (
            <AlertBox variant={result.failed === 0 ? 'success' : 'danger'}>
              Imported <strong>{result.inserted}</strong> {result.inserted === 1 ? 'row' : 'rows'}.
              {result.failed > 0 && ` Failed: ${result.failed}.`}{' '}
              <Link
                to="/admin/inventory"
                style={{ color: 'inherit', textDecoration: 'underline' }}
                onClick={() => void navigate('/admin/inventory')}
              >
                View inventory
              </Link>
            </AlertBox>
          )}

          <div
            style={{
              border: '1px solid var(--admin-hairline, var(--border))',
              borderRadius: 'var(--r-card, 0.5rem)',
              overflow: 'hidden',
              background: 'var(--surface-elev)',
            }}
          >
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '0.85rem',
                }}
              >
                <thead>
                  <tr
                    style={{
                      background: 'var(--surface-muted)',
                      borderBottom: '1px solid var(--admin-hairline, var(--border))',
                    }}
                  >
                    <th style={thStyle}>Row</th>
                    <th style={thStyle}>Title</th>
                    <th style={thStyle}>Category</th>
                    <th style={thStyle}>Consignor</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Qty</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Start</th>
                    <th style={thStyle}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 100).map((row) => (
                    <tr
                      key={row.rowNumber}
                      style={{
                        borderBottom: '1px solid var(--admin-hairline, var(--border))',
                        background:
                          row.errors.length > 0 ? 'var(--surface-danger, #fef2f2)' : undefined,
                      }}
                    >
                      <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>{row.rowNumber}</td>
                      <td style={tdStyle}>{row.preview.title}</td>
                      <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>
                        {row.preview.category}
                      </td>
                      <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>
                        {row.preview.consignor}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{row.preview.qty}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{row.preview.startingBid}</td>
                      <td style={tdStyle}>
                        {row.errors.length === 0 ? (
                          <Badge variant="success">OK</Badge>
                        ) : (
                          <span
                            style={{
                              color: 'var(--text-danger, #991b1b)',
                              fontSize: '0.8rem',
                            }}
                          >
                            {row.errors.join(' ')}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {rows.length > 100 && (
              <div
                style={{
                  padding: '0.5rem 0.75rem',
                  color: 'var(--text-muted)',
                  fontSize: '0.8rem',
                  background: 'var(--surface-muted)',
                  borderTop: '1px solid var(--admin-hairline, var(--border))',
                }}
              >
                Showing first 100 rows of {rows.length}. Full file will be imported on click.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function AlertBox({
  variant,
  children,
}: {
  variant: 'danger' | 'success';
  children: React.ReactNode;
}) {
  const palette =
    variant === 'danger'
      ? {
          bg: 'var(--surface-danger, #fef2f2)',
          fg: 'var(--text-danger, #991b1b)',
          border: 'var(--border-danger, #fecaca)',
        }
      : {
          bg: 'var(--surface-success, #f0fdf4)',
          fg: 'var(--text-success, #166534)',
          border: 'var(--border-success, #bbf7d0)',
        };
  return (
    <div
      role={variant === 'danger' ? 'alert' : 'status'}
      style={{
        padding: '0.75rem 1rem',
        borderRadius: 'var(--r-tile, 0.375rem)',
        background: palette.bg,
        color: palette.fg,
        border: `1px solid ${palette.border}`,
        marginBottom: '1rem',
        fontSize: '0.875rem',
      }}
    >
      {children}
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: '0.45rem 0.6rem',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: '0.72rem',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  color: 'var(--text-muted)',
};

const tdStyle: React.CSSProperties = {
  padding: '0.45rem 0.6rem',
  verticalAlign: 'middle',
};

const selectStyle: React.CSSProperties = {
  padding: '0.35rem 0.6rem',
  borderRadius: 'var(--r-tile, 0.375rem)',
  border: '1px solid var(--admin-hairline, var(--border))',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: '0.875rem',
};
